<script>
  // @ts-check
  import { breakpointValues } from "../../utilities/breakpoints";
  import Button from "../../atoms/Button.svelte";
  import Icon from "../../atoms/Icon.svelte";
  import CarouselSlide from "./CarouselSlide.svelte";
  import GridContainer from "../../atoms/GridContainer.svelte";
  import { onMount } from "svelte";

  /** @type { string } */
  let className = "";
  export { className as class };

  /**
   * Primarily used for testing
   * @type { string }
   * */
  export let id = undefined;

  /**
   * @type { Array<{text: string, srcset: string, aspectRatio: string, sizes: string, image: string, altText: string}> }
   * */
  export let data = [];

  /**
   * @type { "portrait" | "landscape" | "mixed" }
   *  */
  export let variant = "portrait";

  /**
   * The aria-label of the button. Must be provided when using Icon only button.
   * @type {string}
   */
  export let ariaLabel = undefined;

  const NUM_SLIDES = { single: 1.2, multiple: 2.4 };

  // Initialization
  let currentIndex = 0;
  let offset = 0;
  let slideItemWidth = 300;
  let totalSlides = data?.length;
  let slidesToShow = NUM_SLIDES.multiple;
  let disabledNext = false;
  let screenWidth = 0;

  let slideWindow;
  let slideList;
  let pageWidth;
  let newOffset;

  let slidesToStop; // To get where to stop the list and remove the border
  let slideListPadding; // To add a right padding in the end of the slide list

  let scrollX = 0;
  let carouselContainerScrollClasses = "";
  let slideListScrollClasses = "";
  let slideScrollClasses = "";

  function makeSwipeable() {
    carouselContainerScrollClasses = "";
    slideListScrollClasses =
      "snap-x snap-mandatory pb-2 scroll-pl-0 overflow-y-hidden overflow-x-scroll";
    slideScrollClasses = "snap-start snap-always";
  }

  function makePaginatable() {
    carouselContainerScrollClasses =
      "snap-mandatory snap-start scroll-pl-7.5 overflow-hidden lg:scroll-pl-15";
    slideListScrollClasses = "";
    slideScrollClasses = "";
  }

  function initInteractionMode(showPagination) {
    // reset
    currentIndex = 0;
    offset = 0;
    scrollX = 0;

    if (slideList) {
      slideList.scrollLeft = 0;
      document.querySelector('div[aria-roledescription="carousel"]').scrollLeft = 0;
    }

    // set interaction mode
    if (showPagination) {
      makePaginatable();
    } else {
      makeSwipeable();
    }
  }

  function handleScrollEnd() {
    const scrollingLeft = slideList.scrollLeft < scrollX;
    if (scrollingLeft) {
      currentIndex--;
    } else {
      currentIndex++;
    }

    scrollX = slideList.scrollLeft;
  }

  $: showPagination = screenWidth >= breakpointValues.lg;
  $: {
    initInteractionMode(showPagination);

    if (screenWidth < breakpointValues.md) {
      slidesToStop = 1;
      slideListPadding = 30;
      slidesToShow = NUM_SLIDES.single;
    } else {
      slidesToStop = variant == "portrait" ? 2 : 1;
      slideListPadding = 60;
      slidesToShow = variant == "portrait" ? NUM_SLIDES.multiple : NUM_SLIDES.single;
    }

    // Guarantee that will show the expected number of slides independent on screen resolution
    slideItemWidth = slideList?.offsetWidth / slidesToShow;

    setOffset();
  }

  const setOffset = () => {
    let listWidth = 0;
    // Check if is the first to prevent a negative offset onMount the carousel
    newOffset = currentIndex === 0 ? 0 : slideList?.children[currentIndex].offsetLeft * -1;

    // Get the size of space we can view
    pageWidth = slideWindow?.offsetWidth;

    // Calculates the length of the entire list
    for (let i = 0; i < totalSlides; i++) {
      listWidth += variant !== "mixed" ? slideItemWidth : slideList?.children[i].offsetWidth;
    }

    // If get in the end of the list only walk the missing piece
    if (currentIndex === 0) {
      offset = 0;
    } else if (newOffset + listWidth > pageWidth) {
      offset = newOffset;
    } else {
      offset = pageWidth - listWidth - slideListPadding;
    }
  };

  const goToNext = () => {
    currentIndex++;
    setOffset();
    if (currentIndex === totalSlides - slidesToStop) {
      disabledNext = true;
    }
  };

  const goToPrev = () => {
    currentIndex--;
    setOffset();
    disabledNext = false;
  };

  onMount(() => {
    if (slideList) {
      slideList.scrollLeft = 0;
      document.querySelector('div[aria-roledescription="carousel"]').scrollLeft = 0;
    }
  });

  $: ariaLiveRegionText = `Image ${currentIndex + 1} of ${totalSlides}.`;
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#if data?.length}
  <GridContainer {id} variant="bleed" containerClass={className}>
    <div bind:this={slideWindow} class="pl-7.5 lg:pl-15 {carouselContainerScrollClasses}">
      <div
        bind:this={slideList}
        aria-live="polite"
        aria-label={ariaLabel}
        aria-roledescription="carousel"
        class="transition-translate whitespace-nowrap duration-500 ease-in-out {slideListScrollClasses} {variant !==
        'mixed'
          ? 'flex'
          : null}"
        on:scrollend={handleScrollEnd}
      >
        {#each data as { text, image, altText, srcset, sizes }, index (index)}
          {@const pseudoElement = `before:content-[''] before:h-full before:scale-y-[10] before:absolute before:w-[1px] before:bg-black before:top-[0px] before:-left-[0.9375rem] md:before:-left-4.5 lg:before:-left-9 first-of-type:before:hidden`}
          <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
          <div
            class="
              relative inline-block {slideScrollClasses}
              {currentIndex === index &&
            currentIndex !== 0 &&
            currentIndex !== totalSlides - slidesToStop
              ? ''
              : pseudoElement}"
            tabindex={showPagination ? -1 : 0}
            aria-label="Image {index + 1} of {totalSlides}"
            on:focus={() => {
              currentIndex = index;
            }}
          >
            <CarouselSlide
              {text}
              {altText}
              {image}
              {variant}
              {index}
              {slideItemWidth}
              {totalSlides}
              {srcset}
              {sizes}
            />
          </div>
        {/each}
      </div>
    </div>

    {#if showPagination}
      <div aria-controls={id} class="mt-8 flex items-center gap-x-3 pl-7.5 lg:mt-10 lg:pl-15">
        <Button on:click={goToPrev} disabled={currentIndex === 0} ariaLabel="previous">
          <Icon slot="icon" name="arrowLeft" />
        </Button>
        <Button on:click={goToNext} disabled={disabledNext} ariaLabel="next">
          <Icon slot="icon" name="arrowRight" />
        </Button>
      </div>

      <span aria-live="polite" class="sr-only">
        {ariaLiveRegionText}
      </span>
    {/if}
  </GridContainer>
{/if}
